import { useEntitlement } from '@/hooks/useEntitlement';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { StepIndicator } from '@/components/ui/StepIndicator';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { OnboardingUserType } from '@/services/onboarding.service';
import { WelcomeStep } from './wizard-steps/WelcomeStep';
import { ConnectChannelStep } from './wizard-steps/ConnectChannelStep';
import { CreateCalendarStep } from './wizard-steps/CreateCalendarStep';
import { VariationsStep } from './wizard-steps/VariationsStep';
import { ReadyStep } from './wizard-steps/ReadyStep';
import { AgencyWelcomeStep } from './wizard-steps/AgencyWelcomeStep';
import { AgencyCreateClientStep } from './wizard-steps/AgencyCreateClientStep';
import { cn } from '@/utils/cn';
import { ICONS } from '@/constants/icons';
import { useChannels } from '@/hooks/useChannels';
import { useCalendars, useCreateCalendar } from '@/hooks/useCalendar';
import { useAuth } from '@/hooks/useAuth';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { useUpdateVariationSettings } from '@/hooks/useVariationSettings';
import { usePackage } from '@/hooks/usePackage';
import channelsApi from '@/services/channels';
import { toast } from '@/utils/toast';
import { isPlatformNotAvailableError } from '@/services/api';
import { isPurchaseReturnLocation, isSettingsLocation } from '@/utils/earlySupporterFormat';

/**
 * SetupWizard Component
 *
 * Full-screen modal wizard for initial onboarding.
 * Guides freemium users through essential setup:
 * 1. Welcome - Select user type
 * 2. Connect Channel - Connect first social account
 * 3. Create Calendar - Set up first calendar
 * 4. Variations - Configure content variations (optional)
 * 5. Ready - Completion summary with CTAs
 *
 * Features:
 * - Step indicator at top
 * - Back/Skip/Continue navigation
 * - Keyboard accessible (Escape to skip)
 * - Smooth transitions between steps
 * - Auto-saves progress
 */

type WizardStep = 'welcome' | 'connect' | 'calendar' | 'variations' | 'ready';
type AgencyWizardStep = 'agency_welcome' | 'agency_create_client' | 'ready';

interface StepConfig {
  id: WizardStep | AgencyWizardStep;
  label: string;
}

const STEPS_WITH_VARIATIONS: StepConfig[] = [
  { id: 'welcome', label: '' },
  { id: 'connect', label: '' },
  { id: 'calendar', label: '' },
  { id: 'variations', label: '' },
  { id: 'ready', label: '' },
];

const STEPS_WITHOUT_VARIATIONS: StepConfig[] = [
  { id: 'welcome', label: '' },
  { id: 'connect', label: '' },
  { id: 'calendar', label: '' },
  { id: 'ready', label: '' },
];

const AGENCY_STEPS: StepConfig[] = [
  { id: 'agency_welcome', label: '' },
  { id: 'agency_create_client', label: '' },
  { id: 'ready', label: '' },
];

export function SetupWizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Hold the wizard on purchase-return surfaces. BillingSettings removes `purchase=success`
  // when the user dismisses the ready banner, so keep holding until they leave Settings.
  const onPurchaseReturn = isPurchaseReturnLocation(location);
  const [purchaseReturnHold, setPurchaseReturnHold] = useState(false);
  if (onPurchaseReturn && !purchaseReturnHold) {
    setPurchaseReturnHold(true);
  } else if (purchaseReturnHold && !onPurchaseReturn && !isSettingsLocation(location)) {
    setPurchaseReturnHold(false);
  }
  const isPlanReturnPage = onPurchaseReturn || purchaseReturnHold;
  const {
    showWizard,
    variant,
    progress,
    completeWizard,
    skipWizard,
    updateWizardStep,
    setTeamType,
  } = useOnboarding();

  const { user, refreshUser } = useAuth();
  const { isInGodMode } = useEffectiveTeamId();
  const { data: existingChannels = [] } = useChannels();
  const { data: existingCalendars = [] } = useCalendars();
  const createCalendar = useCreateCalendar();
  const updateVariationSettings = useUpdateVariationSettings();
  const { data: packageData, isLoading: isPackageLoading } = usePackage();

  // Get effective teamId from user (handles God Mode)
  const teamId = user?.currentTeamId || user?.teamId;

  // Determine whether variations are available for this team's package.
  // While the package is still loading we default to INCLUDING the step so
  // we never flash it and then remove it — once resolved, if maxVariations
  // is exactly 0, the step is excluded.
  const variationsAllowed =
    isPackageLoading ||
    !packageData ||
    (packageData.package.maxVariations !== 0);

  // Active step list depends on variant and package limits
  const activeSteps =
    variant === 'agency'
      ? AGENCY_STEPS
      : variationsAllowed
        ? STEPS_WITH_VARIATIONS
        : STEPS_WITHOUT_VARIATIONS;

  // ============================================================================
  // Local State
  // ============================================================================

  const [currentStep, setCurrentStep] = useState<WizardStep | AgencyWizardStep>(
    variant === 'agency' ? 'agency_welcome' : 'welcome'
  );
  const [selectedUserType, setSelectedUserType] = useState<OnboardingUserType | null>(null);
  const [connectedChannels] = useState<string[]>([]);
  const [calendarName, setCalendarName] = useState('My Content Calendar');
  const [calendarColor, setCalendarColor] = useState('#3B82F6');
  const [timezone, setTimezone] = useState('Europe/Berlin');
  const [calendarError, setCalendarError] = useState('');
  const [variationType, setVariationType] = useState<'none' | 'languages' | 'platforms' | 'both'>('none');
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [autoTranslateEnabled, setAutoTranslateEnabled] = useState(false);
  // auto_translate locked by the plan: never send it as enabled
  const autoTranslateLocked = useEntitlement('auto_translate').locked;

  // ============================================================================
  // Timezone cascade: user → team → empty (not browser)
  // ============================================================================

  useEffect(() => {
    const userTz = user?.timezone?.trim();
    const teamTz = user?.team?.timezone?.trim();
    setTimezone(userTz || teamTz || '');
  }, [user]);

  // ============================================================================
  // Guard: if the active step list shrinks (e.g. package resolves to
  // maxVariations===0 while the user is already on 'variations'), the current
  // step disappears from activeSteps → index -1 → broken navigation.
  // Recover by advancing to the step that now occupies that position, clamped
  // to the last valid index.
  // ============================================================================

  useEffect(() => {
    const idx = activeSteps.findIndex((s) => s.id === currentStep);
    if (idx !== -1) return; // Happy path — nothing to do.

    // Find where 'variations' used to sit in STEPS_WITH_VARIATIONS (index 3).
    // Use that position to land on the step that now fills that slot (e.g. 'ready' at index 3
    // in the 4-step list), clamped so we never exceed the new list length.
    const VARIATIONS_INDEX = 3;
    const recoveryIndex = Math.min(VARIATIONS_INDEX, activeSteps.length - 1);
    setCurrentStep(activeSteps[recoveryIndex].id);
  }, [activeSteps, currentStep]);

  // ============================================================================
  // Restore progress from context
  // ============================================================================

  useEffect(() => {
    if (progress?.wizardCurrentStep !== undefined) {
      const stepIndex = progress.wizardCurrentStep;
      if (stepIndex >= 0 && stepIndex < activeSteps.length) {
        setCurrentStep(activeSteps[stepIndex].id);
      }
    }

    if (progress?.teamType && variant !== 'agency') {
      setSelectedUserType(progress.teamType as OnboardingUserType);

      // If team already has a type set (by another user), skip welcome step
      if (!progress.wizardCompleted && !progress.wizardSkipped && progress.wizardCurrentStep === 0) {
        setCurrentStep(activeSteps[1].id);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress]);

  // ============================================================================
  // Step Navigation
  // ============================================================================

  const getCurrentStepIndex = useCallback(() => {
    return activeSteps.findIndex((step) => step.id === currentStep);
  }, [activeSteps, currentStep]);

  const canGoBack = useCallback(() => {
    return getCurrentStepIndex() > 0;
  }, [getCurrentStepIndex]);

  const canContinue = useCallback(() => {
    switch (currentStep) {
      case 'agency_welcome':
        return true;
      case 'agency_create_client':
        return true;
      case 'welcome':
        return !!selectedUserType;
      case 'connect':
        return connectedChannels.length > 0 || existingChannels.length > 0;
      case 'calendar':
        return existingCalendars.length > 0 || calendarName.trim().length > 0;
      case 'variations':
        return true; // Always allow continue (optional step)
      default:
        return false;
    }
  }, [currentStep, selectedUserType, connectedChannels, calendarName, existingChannels, existingCalendars]);

  const handleBack = useCallback(() => {
    const currentIndex = getCurrentStepIndex();
    if (currentIndex > 0) {
      const prevStep = activeSteps[currentIndex - 1];
      setCurrentStep(prevStep.id);
      updateWizardStep(currentIndex - 1);
    }
  }, [activeSteps, getCurrentStepIndex, updateWizardStep]);

  const handleNext = useCallback(async () => {
    // Validate current step
    if (!canContinue()) {
      if (currentStep === 'calendar' && !calendarName.trim()) {
        setCalendarError(t('onboarding_wizard_error_calendar_name', { defaultValue: 'Please enter a calendar name' }));
      }
      return;
    }

    const currentIndex = getCurrentStepIndex();

    // Save progress for current step
    if (currentStep === 'welcome' && selectedUserType) {
      await setTeamType(selectedUserType);
    }

    // Create calendar when leaving the calendar step
    if (currentStep === 'calendar' && calendarName.trim()) {
      try {
        await createCalendar.mutateAsync({
          name: calendarName.trim(),
          color: calendarColor,
          isDefault: existingCalendars.length === 0,
        });
      } catch {
        setCalendarError(t('onboarding_wizard_error_calendar_create', { defaultValue: 'Failed to create calendar. Please try again.' }));
        return;
      }
    }

    // Save variation settings when leaving the variations step (only reached when variationsAllowed)
    if (currentStep === 'variations' && teamId && variationsAllowed) {
      const variationsEnabled = variationType !== 'none';
      await updateVariationSettings.mutateAsync({
        teamId,
        data: {
          variationsEnabled,
          variationLanguages: variationsEnabled ? selectedLanguages : [],
          autoTranslateEnabled: variationsEnabled && autoTranslateEnabled && !autoTranslateLocked,
        },
      });
      // Refresh user data so sidebar gets updated team.variationsEnabled
      await refreshUser();
    }

    // Move to next step
    if (currentIndex < activeSteps.length - 1) {
      const nextStep = activeSteps[currentIndex + 1];
      setCurrentStep(nextStep.id);
      await updateWizardStep(currentIndex + 1);
    }
  }, [
    activeSteps,
    canContinue,
    currentStep,
    getCurrentStepIndex,
    selectedUserType,
    calendarName,
    calendarColor,
    calendarError,
    existingCalendars,
    createCalendar,
    setTeamType,
    updateWizardStep,
    teamId,
    variationType,
    selectedLanguages,
    autoTranslateEnabled,
    autoTranslateLocked,
    updateVariationSettings,
    refreshUser,
    variationsAllowed,
  ]);

  const handleSkipWizard = useCallback(async () => {
    await skipWizard();
  }, [skipWizard]);

  // ============================================================================
  // Keyboard Navigation (must be after handleSkipWizard is defined)
  // ============================================================================

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented && showWizard && !isPlanReturnPage) {
        handleSkipWizard();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showWizard, isPlanReturnPage, handleSkipWizard]);

  // Skip just the current step (move to next without performing action)
  const handleSkipStep = useCallback(async () => {
    const currentIndex = getCurrentStepIndex();
    if (currentIndex < activeSteps.length - 1) {
      const nextStep = activeSteps[currentIndex + 1];
      setCurrentStep(nextStep.id);
      await updateWizardStep(currentIndex + 1);
    }
  }, [activeSteps, getCurrentStepIndex, updateWizardStep]);

  const handleComplete = useCallback(async () => {
    await completeWizard();
  }, [completeWizard]);

  // ============================================================================
  // Step Handlers
  // ============================================================================

  const handleSelectUserType = useCallback((type: OnboardingUserType) => {
    setSelectedUserType(type);
  }, []);

  const handleConnectChannel = useCallback(async (platformId: string) => {
    try {
      let authUrl = '';
      switch (platformId) {
        case 'LINKEDIN':
          authUrl = await channelsApi.connectLinkedIn();
          break;
        case 'FACEBOOK':
        case 'INSTAGRAM':
          authUrl = await channelsApi.connectFacebook();
          break;
        case 'YOUTUBE':
          authUrl = await channelsApi.connectYouTube();
          break;
        case 'TIKTOK':
          authUrl = await channelsApi.connectTikTok();
          break;
        case 'PINTEREST':
          authUrl = await channelsApi.connectPinterest();
          break;
        case 'THREADS':
          authUrl = await channelsApi.connectThreads();
          break;
        case 'GOOGLE_MY_BUSINESS':
          authUrl = await channelsApi.connectGoogleMyBusiness();
          break;
        case 'BLUESKY':
          // Bluesky uses app password, not OAuth - skip in wizard, user can connect in Settings
          toast.info(t('onboarding_wizard_info_bluesky', { defaultValue: 'Bluesky can be connected from Settings → Channels' }));
          return;
        default:
          return;
      }
      if (authUrl) {
        window.location.href = authUrl;
      }
    } catch (err) {
      // 403 PLATFORM_NOT_AVAILABLE: the global upsell modal already explains it
      if (isPlatformNotAvailableError(err)) return;
      toast.error(t('onboarding_wizard_error_connect', { defaultValue: 'Failed to start connection. Please try again.' }));
    }
  }, []);

  const handleCalendarNameChange = useCallback((name: string) => {
    setCalendarName(name);
    if (calendarError) {
      setCalendarError('');
    }
  }, [calendarError]);

  const handleCreatePost = useCallback(async () => {
    await handleComplete();
    // Open post creator modal via event
    window.dispatchEvent(new CustomEvent('open-post-creator'));
  }, [handleComplete]);

  const handleExploreCalendar = useCallback(async () => {
    await handleComplete();
    navigate('/calendar');
  }, [handleComplete, navigate]);

  const handleGoToDashboard = useCallback(async () => {
    await handleComplete();
    navigate('/'); // Dashboard is at root
  }, [handleComplete, navigate]);

  const handleGoToAgencyPortfolio = useCallback(async () => {
    await handleComplete();
    navigate('/agency/portfolio');
  }, [handleComplete, navigate]);

  const handleOpenPortfolioFromStep = useCallback(async () => {
    // Close the wizard first, then land on the portfolio with the create-brand modal open.
    await handleComplete();
    navigate('/agency/portfolio?createBrand=1');
  }, [handleComplete, navigate]);

  // ============================================================================
  // Don't render if wizard is not shown or user is in God Mode
  // ============================================================================

  // Don't open over the "Your Plan is ready" celebration; it opens as soon as
  // the user leaves this page (nothing is persisted, showWizard stays true).
  if (!showWizard || isInGodMode || isPlanReturnPage) {
    return null;
  }

  // ============================================================================
  // Render
  // ============================================================================

  const currentStepIndex = getCurrentStepIndex();
  const isReadyStep = currentStep === 'ready';
  const isFirstStep = currentStep === 'welcome' || currentStep === 'agency_welcome';

  return (
    <Modal
      open={showWizard}
      onOpenChange={() => {}} // Prevent closing by clicking overlay
      size="md"
      raw
      preventClose
    >
      <div className="flex flex-col h-full max-h-[90vh]">
        {/* Hidden focus trap to prevent auto-focus on first button */}
        <div tabIndex={0} className="sr-only focus:outline-none" aria-hidden="true" />

        {/* Header with Step Indicator */}
        <div className="flex-shrink-0 px-6 pt-6 pb-4">
          {!isReadyStep && (
            <StepIndicator
              steps={activeSteps.map((step) => ({
                id: step.id,
                label: t(`onboarding_step_${step.id}`, { defaultValue: step.id }),
              }))}
              currentStepIndex={currentStepIndex}
              className="mb-4"
            />
          )}
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {currentStep === 'welcome' && (
            <WelcomeStep
              selectedType={selectedUserType}
              onSelectType={handleSelectUserType}
            />
          )}

          {currentStep === 'connect' && (
            <ConnectChannelStep
              connectedChannels={connectedChannels}
              existingChannels={existingChannels}
              onConnectChannel={handleConnectChannel}
            />
          )}

          {currentStep === 'calendar' && (
            <CreateCalendarStep
              calendarName={calendarName}
              calendarColor={calendarColor}
              timezone={timezone}
              existingCalendars={existingCalendars}
              onCalendarNameChange={handleCalendarNameChange}
              onCalendarColorChange={setCalendarColor}
              onTimezoneChange={setTimezone}
              error={calendarError}
            />
          )}

          {currentStep === 'variations' && (
            <VariationsStep
              variationType={variationType}
              selectedLanguages={selectedLanguages}
              autoTranslateEnabled={autoTranslateEnabled}
              onVariationTypeChange={setVariationType}
              onLanguagesChange={setSelectedLanguages}
              onAutoTranslateChange={setAutoTranslateEnabled}
            />
          )}

          {currentStep === 'agency_welcome' && (
            <AgencyWelcomeStep />
          )}

          {currentStep === 'agency_create_client' && (
            <AgencyCreateClientStep
              onOpenPortfolio={handleOpenPortfolioFromStep}
            />
          )}

          {currentStep === 'ready' && variant === 'agency' && (
            <ReadyStep
              completedItems={[
                {
                  label: t('onboarding_agency_ready_workspace', { defaultValue: 'Brand Portfolio configured' }),
                },
                {
                  label: t('onboarding_agency_ready_command_center', { defaultValue: 'Brand portfolio ready' }),
                },
              ]}
              onCreatePost={handleGoToAgencyPortfolio}
              onExploreCalendar={handleExploreCalendar}
              onGoToDashboard={handleGoToDashboard}
              primaryActionLabel={t('onboarding_agency_ready_cta', { defaultValue: 'Go to your Brand Portfolio' })}
              primaryActionIcon={ICONS.ARROW_RIGHT}
            />
          )}

          {currentStep === 'ready' && variant !== 'agency' && (
            <ReadyStep
              completedItems={[
                {
                  icon: 'user',
                  label: t('onboarding_ready_account_type', { defaultValue: '{{type}} account', type: selectedUserType ?? '' }),
                },
                {
                  icon: 'linkedin',
                  label: existingChannels.length > 0
                    ? t('onboarding_ready_channels_ready', { defaultValue: '{{count}} channel ready', count: existingChannels.length + connectedChannels.length })
                    : t('onboarding_ready_channels_connected', { defaultValue: '{{count}} channel connected', count: connectedChannels.length }),
                },
                {
                  icon: 'calendar',
                  label: existingCalendars.length > 0 && calendarName === 'My Content Calendar'
                    ? t('onboarding_ready_calendar_existing', { defaultValue: 'Using existing calendar', count: existingCalendars.length })
                    : t('onboarding_ready_calendar_created', { defaultValue: '"{{name}}" calendar created', name: calendarName }),
                },
                ...(variationType !== 'none' ? [{
                  icon: ICONS.LANGUAGE,
                  label: variationType === 'languages'
                    ? t('onboarding_ready_variation_languages', { defaultValue: 'Language variations ({{languages}})', languages: selectedLanguages.join(', ') })
                    : variationType === 'platforms'
                    ? t('onboarding_ready_variation_platforms', { defaultValue: 'Platform variations enabled' })
                    : t('onboarding_ready_variation_both', { defaultValue: 'Language & platform variations ({{languages}})', languages: selectedLanguages.join(', ') }),
                }] : []),
              ]}
              onCreatePost={handleCreatePost}
              onExploreCalendar={handleExploreCalendar}
              onGoToDashboard={handleGoToDashboard}
            />
          )}
        </div>

        {/* Fixed Footer Navigation */}
        {!isReadyStep && (
          <div className="flex-shrink-0 border-t border-gray-200 px-6 py-4 bg-white rounded-b-2xl">
            <div className="flex items-center justify-between">
              {/* Back Button */}
              <Button
                variant="outline"
                size="md"
                onClick={handleBack}
                disabled={!canGoBack()}
                className={cn(!canGoBack() && 'invisible')}
              >
                {t('back')}
              </Button>

              {/* Skip / Continue Buttons */}
              <div className="flex items-center gap-3">
                {isFirstStep ? (
                  <Button
                    variant="outline"
                    size="md"
                    onClick={handleSkipWizard}
                    className="text-gray-600"
                  >
                    {t('onboarding_wizard_skip_setup')}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="md"
                    onClick={handleSkipStep}
                    className="text-gray-600"
                  >
                    {t('onboarding_skip_for_now')}
                  </Button>
                )}

                <Button
                  variant="primary"
                  size="md"
                  onClick={handleNext}
                  disabled={!canContinue()}
                >
                  {t('continue')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
